import { ArrowBack, FavoriteBorder, Phone, SaveAlt, WebAssetOutlined } from '@material-ui/icons';
import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Slider from 'react-slick';
import { PreviewInPhone } from '~/Components';
import { Map } from '~/Components/Icons/Map';
import { Button, GoogleMap, IPosition, Typography } from '~/UIKit';
import { ImageType } from '~/Models/DTO/Location.models';
import { LocationEditSideSectionStyles } from './LocationEditSideSectionStyles';
import { sortBy } from 'lodash';
import { defaultLatLong } from '~/Constants/Values.constants';
import { WeekDays } from '~/Models/DTO/Provider.model';
import { Grid } from '@mui/material';

interface ILocationEditSideSectionProps {
  title?: string;
  images?: ImageType[];
  description?: string;
  importantInfo?: string;
  coordinates?: IPosition;
  zipCode?: string;
  street?: string;
  city?: string;
  displayWeekdays?: WeekDays;
  openingHoursDescription?: string;
  profileUrl?: string;
}

const defaultUSCImage = [
  {
    imageUrl:
      'https://storage.googleapis.com/download/storage/v1/b/usc-pro-uscweb-live-media/o/de-live%2FvenueHome_1024x576_YogaCompressed%20%281%29_162314705641178_admin.jpg?alt=media',
    order: 1,
  },
];

export const LocationEditSideSection = ({
  title = '',
  images = defaultUSCImage,
  description = '',
  importantInfo = '',
  coordinates = defaultLatLong,
  zipCode = '00000',
  street = '',
  city = '',
  displayWeekdays,
  openingHoursDescription = '',
  profileUrl = '',
}: ILocationEditSideSectionProps) => {
  const { classes } = LocationEditSideSectionStyles();
  const { t } = useTranslation();
  const { lat, lng } = coordinates;
  const [dataModel, setDataModel] = useState({
    description: '',
    title: '',
    street: '',
    zipCode: '',
    city: '',
    importantInfo: '',
  });
  //Refs
  const contentRef = useRef<HTMLDivElement>(null);

  //Memos
  const sortedImages = useMemo<ImageType[]>(() => {
    if (images.length > 0) {
      return sortBy(images, ['order']);
    }
    return defaultUSCImage;
  }, [images]);

  const position = useMemo(() => {
    if (lat && lng) {
      return {
        lat,
        lng,
      };
    }
    return defaultLatLong;
  }, [lat, lng]);

  const weekdays = useMemo(() => {
    if (displayWeekdays) {
      return Object.keys(displayWeekdays);
    }
    return [];
  }, [displayWeekdays]);

  useEffect(() => {
    setTimeout(() => {
      setDataModel({ description, title, street, zipCode, city, importantInfo });
    }, 350);
  }, []);

  useEffect(() => {
    if (
      (Boolean(dataModel.description) && dataModel.description !== description) ||
      (Boolean(dataModel.street) && dataModel.street !== street) ||
      (Boolean(dataModel.zipCode) && dataModel.zipCode !== zipCode) ||
      (Boolean(dataModel.city) && dataModel.city !== city) ||
      (Boolean(dataModel.importantInfo) && dataModel.importantInfo !== importantInfo)
    ) {
      contentRef?.current?.scroll?.({
        top: 1000,
        left: 0,
        behavior: 'smooth',
      });
    } else {
      contentRef?.current?.scroll?.({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });
    }
    setDataModel({ description, title, street, zipCode, city, importantInfo });
  }, [description, title, images, street, zipCode, city, importantInfo]);

  const content = (
    <PreviewInPhone contentRef={contentRef}>
      <div className={classes.LocationEditSideSectionContainer}>
        <Slider
          className={classes.LocationEditSideSectionCarasoul}
          dots
          speed={500}
          slidesToShow={1}
          slidesToScroll={1}
        >
          {sortedImages.map((image, index) => (
            <div key={`USC-image-${index}-${image?.imageUrl?.slice(-10)}`}>
              <div className={classes.LocationEditSideSectionCarasoulContainer} />
              <img
                className={classes.LocationEditSideSectionCarasoulItem}
                alt={title}
                title={title}
                src={image.imageUrl}
              />
            </div>
          ))}
        </Slider>
        <h1 className={classes.LocationEditSideSectionTitle}>{title}</h1>

        <div className={classes.LocationEditSideSectionSection}>
          <h3 className={classes.LocationEditSideSectionSectionTitle}>
            {t('settings.title.openingHours')}
          </h3>
          {openingHoursDescription && <div>{openingHoursDescription}</div>}
          {!openingHoursDescription && weekdays && displayWeekdays && (
            <Grid className={classes.LocationEditSideSectionSectionOpeningHours}>
              {weekdays.map(
                (day, dayIndex) =>
                  displayWeekdays[day].length > 0 && (
                    <Grid container key={dayIndex}>
                      <Grid xs={6}>
                        <Typography className={classes.LocationEditSideSectionSectionDayLabel}>
                          {`${t(`app.weekdays.${day}`)}: `}
                        </Typography>
                      </Grid>
                      <Grid xs={6}>
                        {displayWeekdays[day].map((timeSlot, timeSlotIndex) => (
                          <div key={timeSlotIndex}>
                            {`${timeSlot.from
                              .toString()
                              .substring(0, 5)} - ${timeSlot.to.toString().substring(0, 5)}`}
                          </div>
                        ))}
                      </Grid>
                    </Grid>
                  ),
              )}
            </Grid>
          )}
        </div>

        <div className={classes.LocationEditSideSectionSection}>
          <h3 className={classes.LocationEditSideSectionSectionTitle}>
            {t('form.field.location.address')}
          </h3>
          <div className={classes.LocationEditSideSectionSectionDescription}>
            <GoogleMap
              containerStyle={{ height: 60, width: '100%' }}
              position={position}
              defaultZoom={15}
              options={{
                mapTypeControl: false,
                fullscreenControl: false,
                zoomControl: false,
                gestureHandling: 'cooperative',
              }}
              staticMarker
            />
            <div>
              <div className={classes.LocationEditSideSectionMapDescription}>
                {street && (
                  <p className={classes.LocationEditSideSectionSectionDescription}>{street}</p>
                )}
                {zipCode && city && (
                  <p
                    className={classes.LocationEditSideSectionSectionDescription}
                  >{`${zipCode} ${city}`}</p>
                )}
              </div>
              <div className={classes.LocationEditSideSectionMapDescriptionButtonContainer}>
                <Button className={classes.LocationEditSideSectionMapDescriptionButton}>
                  <Phone /> Call
                </Button>
                <Button className={classes.LocationEditSideSectionMapDescriptionButton}>
                  <WebAssetOutlined /> Website
                </Button>
                <Button className={classes.LocationEditSideSectionMapDescriptionButton}>
                  <Map /> Directions
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className={classes.LocationEditSideSectionSection}>
          <h3 className={classes.LocationEditSideSectionSectionTitle}>{t('event.description')}</h3>
          <p className={classes.LocationEditSideSectionSectionDescription}>{description}</p>
        </div>

        <div className={classes.LocationEditSideSectionSection}>
          <h3 className={classes.LocationEditSideSectionSectionTitle}>{t('usc.importantInfo')}</h3>
          <p className={classes.LocationEditSideSectionSectionDescription}>{importantInfo}</p>
        </div>
        <button className={classes.LocationEditSideSectionButton}>{t('usc.coursePlan')}</button>

        <div className={classes.LocationEditSideSectionL}>L</div>
        <div className={classes.LocationEditSideSectionXL}>XL</div>

        <ArrowBack className={classes.LocationEditSideSectionBackArrow} />
        <FavoriteBorder className={classes.LocationEditSideSectionFavoriteIcon} />
        <SaveAlt className={classes.LocationEditSideSectionSaveIcon} />
      </div>
    </PreviewInPhone>
  );

  return profileUrl?.length ? (
    <div className={classes.columnView}>
      <Button
        variant="outlined"
        size="medium"
        className={classes.button}
        onClick={() => window.open(profileUrl, '_blank')}
      >
        {t('app.location.link.profile')}
      </Button>
      {content}
    </div>
  ) : (
    content
  );
};
